import type { AjaxError, AjaxResponse } from 'rxjs';
import { post } from '@helpcenter/ajax';
import { Observable } from '@helpcenter/epics-rxjs';
import { isJsmResetPasswordKeepRedirectionDestinationEnabled } from '@helpcenter/feature-flags';
import type { AuthData } from 'state/actions/login/login';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { operationalEvent } from '@atlassian/help-center-common-util/analytics/events';
import { getEnv } from '@atlassian/help-center-common-util/env';
import type { Env } from '@atlassian/help-center-common-util/env';
import { contextPath, getBaseName } from '@atlassian/help-center-common-util/history';
import { origin } from '@atlassian/help-center-common-util/location';
import type { LoginType } from '@atlassian/help-center-common-util/login';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import { prependWithSlashIfAbsent, getHelpCenterUrl } from '@atlassian/help-center-common-util/url';

interface IdentityLoginPayload {
    key: string;
    context: string;
}
interface AuthMethodResponse {
    authUrl?: string;
    authType: 'PASSWORD' | 'SSO';
}
export interface CheckLoginActionResponse {
    action: LoginType;
    methods: AuthMethodResponse[];
}

// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
// eslint-disable-next-line no-shadow
export enum LoginAttemptErrorType {
    INVALID_CREDENTIALS,
    ACCOUNT_LOCKED_ERROR,
    MISC_LOGIN_ERROR,
}

export interface LoginAttemptError {
    errorType: LoginAttemptErrorType;
}

export interface LoginAttemptResponse {
    loginSucceeded: boolean;
    error: LoginAttemptError | null;
}

const createOperationalPayload = (actionSubject: 'identity' | 'jira', attributes: Record<string, string | boolean>) => {
    return {
        attributes,
        actionSubject,
        action: 'login',
        source: 'unknownSource',
    };
};

export const checkLoginAction = (params: {
    email: string;
    authData: AuthData;
    awsWafToken?: string;
    context?: string;
}): Observable<CheckLoginActionResponse> => {
    const { email, authData, awsWafToken, context = contextPath } = params;
    const uri = `${context}/rest/servicedesk/customer-management/1/twosteplogin/action`;
    const body = JSON.stringify({
        email,
        ...authData,
    });

    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(FeatureGates.checkGate('fetch_aws_waf_token_before_login') && awsWafToken
            ? { 'x-aws-waf-token': `${awsWafToken}` }
            : {}),
    };

    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return post(uri, body, headers).map((ajaxResponse) => ajaxResponse.response);
};

export const forgotPassword = (resetUrl: string, email: string, redirectUrl?: string) => {
    const uri = prependWithSlashIfAbsent(resetUrl);
    const body = createBodyForForgotPassword(email, redirectUrl);
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    return post(uri, body, headers);
};

/*
    This is a duplicate of forgotPassword as we want to show the same form but with a slightly different
    UX for instances where the user has never logged in before.

    This comes as a quick fix to improve the signup flow for new (not previously logged in) users:
    https://hello.atlassian.net/wiki/spaces/~jafrench/pages/5788841309/BEARS-295+-+Fix+CA+login+signup+flow#Option-2---Quick-and-dirty

    A more robust solution will involve backend changes however this is TBC
*/
export const createPassword = (resetUrl: string, email: string) => {
    const uri = prependWithSlashIfAbsent(resetUrl);
    const body = createBodyForForgotPassword(email);
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    return post(uri, body, headers);
};

const createBodyForForgotPassword = (usernameOrEmail: string, redirectUrl?: string) => {
    if (isJsmResetPasswordKeepRedirectionDestinationEnabled()) {
        return JSON.stringify({
            usernameOrEmail,
            redirectUrl,
        });
    }
    return JSON.stringify({
        usernameOrEmail,
    });
};

const responseKeyToErrorType = (loginPayload: IdentityLoginPayload): LoginAttemptErrorType => {
    if (loginPayload) {
        switch (loginPayload.key) {
            case 'invalid-user-credentials':
                return LoginAttemptErrorType.INVALID_CREDENTIALS;
            case 'blocked-user-credentials':
                return LoginAttemptErrorType.ACCOUNT_LOCKED_ERROR;
            default:
        }
    }

    return LoginAttemptErrorType.MISC_LOGIN_ERROR;
};

// Identity error responses: https://stash.atlassian.com/projects/IPS/repos/customer-account-directory/browse/lib/common/error-keys.js
export const handleIdentityLoginResponse = (ajaxError: AjaxError) => {
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const ajaxResponse: IdentityLoginPayload = ajaxError.response;

    if (ajaxError.status === 403) {
        const errorType = responseKeyToErrorType(ajaxResponse);
        if (errorType === LoginAttemptErrorType.MISC_LOGIN_ERROR) {
            reportError(ajaxError, { message: JSON.stringify(ajaxResponse) });
        }

        return {
            loginSucceeded: false,
            error: {
                errorType,
            },
        };
    }

    reportError(ajaxError, { message: JSON.stringify(ajaxResponse) });
    throw ajaxError;
};

export const getDefaultContinueParam = () => `${origin()}${getBaseName()}${getHelpCenterUrl()}`;

export const loginUsingIdentityV2 = (params: {
    email: string;
    password: string;
    continueParam: string | null;
    context?: string;
}): Observable<LoginAttemptResponse> => {
    const { email, password, context = contextPath } = params;

    const continueParam = params.continueParam ? params.continueParam : getDefaultContinueParam();

    const uri = `${context}/jsd-login-v2/v2/customer/login/authenticate?continue=${continueParam}`;
    const body = { email, password };
    const headers = {
        'Content-Type': 'application/json',
    };

    let edition: string | undefined;
    if (FeatureGates.checkGate('edition_available_in_help_center')) {
        const envWithEdition = getEnv() as Env & { edition?: string };
        edition = envWithEdition.edition;
    }

    return post(uri, body, headers)
        .do(
            (ajaxResponse: AjaxResponse) => {
                operationalEvent(
                    createOperationalPayload('identity', {
                        success: true,
                        status: ajaxResponse.status.toString(),
                        traceId: ajaxResponse.xhr.getResponseHeader('atl-traceid') || 'unknown',
                        ...(edition ? { edition } : {}),
                    })
                );
            },
            (ajaxError: AjaxError) => {
                // Narrow type for response
                const response = ajaxError.response as Partial<IdentityLoginPayload> | undefined;
                operationalEvent(
                    createOperationalPayload('identity', {
                        success: false,
                        status: ajaxError.status.toString(),
                        errorKey: response?.key || 'unknown',
                        ...(FeatureGates.checkGate('add_error_message_to_event') && {
                            errorMessage:
                                typeof response?.context === 'object' && response.context
                                    ? (response.context as { errorDetail?: string }).errorDetail || 'unknown'
                                    : 'unknown',
                        }),
                        traceId: ajaxError.xhr.getResponseHeader('atl-traceid') || 'unknown',
                        ...(edition ? { edition } : {}),
                    })
                );
            }
        )
        .map(() => ({
            loginSucceeded: true,
            error: null,
        }))
        .catch((ajaxError: AjaxError) => {
            return Observable.of(handleIdentityLoginResponse(ajaxError));
        });
};

export const loginV2 = (email: string, password: string, continueParam: string | null) => {
    return loginUsingIdentityV2({ email, password, continueParam });
};
